/**
 * Darstellung in den Einstellungen: Farbschema und Hintergrund. Die
 * Vorschaukacheln zeigen jedes Muster im gerade aktiven Modus. Die Wahl gilt
 * für das Konto, außer das Gerät soll bewusst abweichen.
 */
import { BACKGROUND_CHOICES, THEME_CHOICES, useAppearance } from '../state/appearance.ts';
import type { ThemeChoice } from '../state/appearance.ts';
import { texts } from '../texts/de.ts';
import { AppIcon } from './AppIcon.tsx';
import type { IconName } from './AppIcon.tsx';
import { Panel } from './Panel.tsx';

/** Piktogramm je Farbschema. */
const THEME_ICONS: Readonly<Record<ThemeChoice, IconName>> = { system: 'device', light: 'sun', dark: 'moon' };

/** Auswahl von Farbschema und Hintergrund. */
export function AppearancePanel() {
  const { theme, background, deviceOnly, setTheme, setBackground, setDeviceOnly } = useAppearance();

  return (
    <Panel title={texts.settings.appearance}>
      <p className="-mt-2 mb-3 text-xs text-content-muted">
        {deviceOnly ? texts.settings.appearanceDevice : texts.settings.appearanceAccount}
      </p>
      <div className="mb-4">
        <h3 className="mb-2 text-sm font-medium">{texts.settings.theme}</h3>
        <div
          className="inline-flex rounded-xl border border-border-subtle bg-surface-sunken p-1"
          role="group"
          aria-label={texts.settings.theme}
        >
          {THEME_CHOICES.map((choice) => (
            <button
              key={choice}
              type="button"
              onClick={() => setTheme(choice)}
              aria-pressed={theme === choice}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-colors ${
                theme === choice
                  ? 'bg-surface-raised font-semibold text-accent-strong shadow-sm'
                  : 'text-content-muted hover:text-content'
              }`}
            >
              <AppIcon name={THEME_ICONS[choice]} size={18} />
              {texts.settings.themes[choice]}
            </button>
          ))}
        </div>
      </div>
      <div>
        <h3 className="mb-2 text-sm font-medium">{texts.settings.background}</h3>
        <div
          className="grid grid-cols-3 gap-2 sm:grid-cols-4"
          role="group"
          aria-label={texts.settings.background}
        >
          {BACKGROUND_CHOICES.map((choice) => (
            <button
              key={choice}
              type="button"
              onClick={() => setBackground(choice)}
              aria-pressed={background === choice}
              className={`rounded-xl border p-1.5 text-left text-xs transition-colors ${
                background === choice
                  ? 'border-accent bg-accent-soft font-semibold text-accent-strong'
                  : 'border-border-subtle hover:border-content-muted'
              }`}
            >
              <span
                data-pattern={choice}
                className="pattern-swatch mb-1.5 block h-14 rounded-lg border border-border-subtle"
              >
                <span className="absolute right-2 bottom-2 left-2 block h-4 rounded bg-surface-raised shadow-sm" />
              </span>
              <span className="flex items-center gap-1 px-0.5">
                {background === choice ? <AppIcon name="check" size={14} /> : null}
                {texts.settings.backgrounds[choice]}
              </span>
            </button>
          ))}
        </div>
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={deviceOnly}
          onChange={(event) => setDeviceOnly(event.target.checked)}
          className="h-4 w-4 accent-accent"
        />
        {texts.settings.deviceOnly}
      </label>
    </Panel>
  );
}
